// Live Contact page. GET + POST /api/v1/contact-requests.
// Only client-write surface in V1 — managers see/post on behalf of a client
// via ?client_id=…

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Kicker, KickerRow, PageHeader, Pill } from '../../components/shell';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

interface ContactRow {
  id: string;
  kind: 'deposit_notification' | 'withdrawal_request' | 'general';
  subject: string;
  body: string;
  status: 'open' | 'acknowledged' | 'closed';
  created_at: string;
  resolved_at: string | null;
  by_email: string | null;
  resolved_by_email: string | null;
}

interface ContactPayload {
  client_id: string;
  requests: ContactRow[];
}

const KIND_LABEL: Record<ContactRow['kind'], string> = {
  deposit_notification: 'Deposit notification',
  withdrawal_request: 'Withdrawal request',
  general: 'General inquiry',
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

export default function Contact() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const clientId = params.get('client_id');

  const [data, setData] = useState<ContactPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<ContactRow['kind']>('deposit_notification');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    if (user && user.role !== 'client' && !clientId) {
      navigate('/fund', { replace: true });
    }
  }, [user, clientId, navigate]);

  const load = useCallback(async () => {
    setError(null);
    const url = '/contact-requests/' + (clientId ? `?client_id=${clientId}` : '');
    try {
      setData(await api<ContactPayload>(url));
    } catch (e: any) {
      if (e?.status === 404 && user?.role !== 'client') { navigate('/fund', { replace: true }); return; }
      setError(e?.status === 409 ? 'Your portfolio is being set up. Transactions and reports will appear here once trading begins.' : 'Failed to load contact requests.');
    }
  }, [clientId, user, navigate]);

  useEffect(() => { void load(); }, [load]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim()) return;
    setSubmitting(true);
    setOkMsg(null);
    try {
      const url = '/contact-requests/' + (clientId ? `?client_id=${clientId}` : '');
      await api(url, { method: 'POST', body: { kind, subject, body } });
      setSubject('');
      setBody('');
      setOkMsg('Sent. The desk has been notified.');
      await load();
      setTimeout(() => setOkMsg(null), 4000);
    } catch {
      setError('Failed to submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="view container">
      <PageHeader kicker="CONTACT" title={<>Get in touch with <em>Rev9.</em></>} />

      {error && (
        <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)' }}>{error}</p>
      )}

      <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 80 }}>
        <div>
          <p className="serif-body" style={{ fontSize: 22, margin: '0 0 56px', maxWidth: '46ch' }}>
            <em>For deposit notifications, withdrawal requests, or any other inquiry —</em> your message reaches the desk directly.
          </p>

          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
            <div className="field">
              <label className="field-label">KIND</label>
              <select className="field-input" value={kind} onChange={e => setKind(e.target.value as ContactRow['kind'])}>
                <option value="deposit_notification">Deposit notification</option>
                <option value="withdrawal_request">Withdrawal request</option>
                <option value="general">General inquiry</option>
              </select>
            </div>

            <div className="field">
              <label className="field-label">SUBJECT</label>
              <input
                className="field-input"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="One-line summary"
                required
              />
            </div>

            <div className="field">
              <label className="field-label">MESSAGE</label>
              <textarea
                className="field-input"
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Wire amount, expected arrival, source account…"
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <button type="submit" className="btn" disabled={submitting}>
                {submitting ? 'Submitting…' : <>Submit request <span className="btn-arrow">→</span></>}
              </button>
              {okMsg && (
                <span className="serif-body" style={{ fontStyle: 'italic', color: 'var(--accent)', margin: 0 }}>
                  <em>{okMsg}</em>
                </span>
              )}
            </div>
          </form>
        </div>

        <aside>
          <div style={{ padding: '32px', border: 'var(--hairline)', background: 'var(--tint)' }}>
            <Kicker>DIRECT LINE</Kicker>
            <div className="serif-body" style={{ fontSize: 22, margin: '12px 0 24px' }}>
              <em>desk@rev9.capital</em>
            </div>
            <hr className="rule" style={{ margin: '20px 0' }} />
            <Kicker>OFFICE</Kicker>
            <div className="serif" style={{ fontSize: 16, marginTop: 12 }}>
              Rev9 Capital LLC<br />
              <span style={{ color: 'var(--ink-3)' }}>Miami, Florida · United States</span>
            </div>
          </div>
        </aside>
      </div>

      <section style={{ marginTop: 96 }}>
        <KickerRow kicker="YOUR OPEN REQUESTS" />
        {!data ? (
          <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
                      textTransform: 'uppercase', color: 'var(--ink-3)' }}>Loading…</p>
        ) : data.requests.length === 0 ? (
          <p className="serif-body" style={{ color: 'var(--ink-2)' }}>No requests yet.</p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>DATE</th>
                <th>KIND</th>
                <th>SUBJECT</th>
                <th data-align="right">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {data.requests.map(r => (
                <tr key={r.id}>
                  <td className="num" style={{ fontSize: 13 }}>{fmtDate(r.created_at)}</td>
                  <td><Pill>{KIND_LABEL[r.kind].toUpperCase()}</Pill></td>
                  <td className="serif" style={{ fontSize: 15 }}>{r.subject}</td>
                  <td data-align="right">
                    <Pill tone={r.status === 'closed' ? '' : 'accent'}>{r.status.toUpperCase()}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <style>{`
        @media (max-width: 1000px) {
          .contact-grid { grid-template-columns: 1fr !important; gap: 56px !important; }
        }
      `}</style>
    </div>
  );
}
